import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DocumentViewerModal,
  HelpTip,
  ProcessingProgress,
  ProcessingStatusView,
  errorMessage,
  processingInFlight,
  processingParamsFrom,
  processingSearch,
  type RoomDocument,
} from '@dataroom/shared';
import {
  useProcessingEstimate,
  useRoomDocuments,
  useRoomProcessing,
  useSellerRoom,
  useSellerRoomAccess,
} from '@/api/hooks';
import { VersionSuggestionsReview } from '@/components/VersionSuggestionsPanel';

/**
 * The seller's PROCESSING STATUS page (owner ask 2026-10-12): every document in
 * the library (the seller's whole-library view, ADR-0028) with its filing result
 * and background OCR / redaction state, by status and upload date — the same
 * view as the admin's. A document's name (or "View file") opens that document
 * in the same viewer the Documents page uses, right here (owner, 2026-10-22: it
 * used to open the whole Documents list). The "uploads that look like newer
 * versions" bar is shown above, as on the Documents page.
 */
export function ProcessingPage() {
  const access = useSellerRoomAccess();
  if (access.status === 'login') return <Navigate to="/login" replace />;
  if (access.status === 'loading') {
    return (
      <p className="mx-auto w-full max-w-container px-4 py-6 text-sm text-muted-foreground md:px-6">
        Loading…
      </p>
    );
  }
  if (access.status === 'forbidden') return <Navigate to="/" replace />;
  return <ProcessingPageInner roomId={access.roomId} />;
}

function ProcessingPageInner({ roomId }: { roomId: string }) {
  const room = useSellerRoom(roomId);
  const [search, setSearch] = useSearchParams();
  // The document open in the viewer: a row of this page (opened with its copy),
  // or one named by id from the "newer versions" bar (found in the room's
  // listing, which is read only then).
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [opened, setOpened] = useState<RoomDocument | null>(null);
  const roomDocuments = useRoomDocuments(roomId, { enabled: viewingId !== null });
  const params = processingParamsFrom(search);
  const timeZone = room.data?.timezone ?? 'UTC';
  const view = useRoomProcessing(roomId, params, timeZone);
  // The estimate polls itself every ~10 s while work remains; when it last read
  // "nothing left" but the page's counts show a stage running (new documents
  // arrived while it was open), read it again.
  const estimate = useProcessingEstimate(roomId);
  const refetchEstimate = estimate.refetch;
  const estimateLeft = estimate.data?.documentsLeft;
  const counts = view.data?.counts;
  const runningNow = counts ? processingInFlight(counts) : 0;
  const waitingOnPeople = counts
    ? Math.max(counts.needs_filing + counts.failed, counts.needs_review)
    : 0;
  useEffect(() => {
    if (estimateLeft === 0 && runningNow > 0) void refetchEstimate();
  }, [estimateLeft, runningNow, refetchEstimate]);
  // The viewer reads the document live from the polled page, so it follows a
  // stage finishing while it is open.
  const viewingDoc = viewingId
    ? (view.data?.documents.find((d) => d.id === viewingId) ??
      roomDocuments.data?.find((d) => d.id === viewingId) ??
      (opened?.id === viewingId ? opened : null))
    : null;
  const openRow = (d: RoomDocument) => {
    setOpened(d);
    setViewingId(d.id);
  };
  const openById = (documentId: string) => {
    setOpened(null);
    setViewingId(documentId);
  };
  return (
    <div className="mx-auto w-full max-w-container space-y-6 px-4 py-6 md:px-6">
      <VersionSuggestionsReview roomId={roomId} onOpenDocument={openById} />
      <Card>
        <CardHeader>
          <CardTitle>Processing status</CardTitle>
          <p className="text-sm text-muted-foreground">
            Every document in the room: whether it has been filed, and where its OCR and redaction
            stand. Both run in the background — keep uploading while they do.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* The running tally by stage and the time left (owner ask 2026-10-23). */}
          <ProcessingProgress estimate={estimate.data} waitingOnPeople={waitingOnPeople} />
          <ProcessingStatusView
            params={params}
            onChange={(next) => setSearch(processingSearch(next), { replace: true })}
            data={view.data}
            loading={view.isPending}
            error={
              view.error ? (errorMessage(view.error) ?? 'Could not load the documents.') : null
            }
            timeZone={timeZone}
            onOpen={openRow}
            canOpen={(d) => d.downloadReady}
            openLabel={() => 'View file'}
            statusAside={<HelpTip id="seller.processing.status-buttons" />}
            redactionAside={<HelpTip id="seller.processing.redaction-indicator" />}
          />
        </CardContent>
      </Card>
      {viewingDoc ? (
        <DocumentViewerModal
          key={viewingDoc.id}
          roomId={roomId}
          document={viewingDoc}
          onClose={() => setViewingId(null)}
          audience="seller"
          redactedCaptionAside={<HelpTip id="seller.documents.redacted-caption" />}
        />
      ) : null}
    </div>
  );
}
